import React, { useEffect, useRef, useState } from "react";
import mapDatas from "./mapData.json";
import "./map.css";
import markerIcon from "../../assets/img/icons/circle_icon.ico";
import unionMarker from "../../assets/img/icons/union_marker.ico";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";

const buttonLabels = {
  Button1: "HEALTHCARE",
  Button2: "RESIDENTIAL",
  Button3: "DEVELOPMENTS",
  Button4: "MUNICIPAL",
  Button5: "HOTEL",
  Button6: "BANKS",
  Button7: "RETAIL",
  Button8: "FOOD & DRINK",
};

const GoogleMapComponent = () => {
  const mapRef = useRef(null); // Reference to the map container

  const [selectedButtonArray, setSelectedButtonArray] = useState({
    Button1: true,
    Button2: true,
    Button3: true,
    Button4: true,
    Button5: true,
    Button6: true,
    Button7: true,
    Button8: true,
  });
  const [markers, setMarkers] = useState([]);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);

  const datas = mapDatas.data;

  const ButtonClick = (event) => {
    setSelectedButtonArray((prevSelectedButtonArray) => ({
      ...prevSelectedButtonArray,
      [event.target.name]: !prevSelectedButtonArray[event.target.name],
    }));
  };
  console.log(selectedButtonArray);
  const getButtonClass = (buttonName) => {
    const baseClass =
      "w-1/5 max-w-[281px] px-3 py-2 text-[#ad885d] rounded-full transition-colors duration-300";

    const activeClass = "bg-[#ad885d] text-white border-2 border-[#ad885d]";

    const inactiveClass =
      "bg-[#ffffff] hover:text-[#ad885d] border-2 border-[#ad885d]";

    return `${baseClass} ${
      selectedButtonArray[buttonName] ? activeClass : inactiveClass
    }`;
  };

  useEffect(() => {
    let isMounted = true;

    const initializeMap = async () => {
      try {
        // Load Google Maps if not already loaded
        if (!window.google || !window.google.maps) {
          await new Promise((resolve) => loadGoogleMaps(resolve));
        }

        if (!isMounted) return;

        // Import required libraries
        const { Map } = await window.google.maps.importLibrary("maps");
        const { Marker } = await window.google.maps.importLibrary("marker");

        // Create map instance
        const mapInstance = new Map(mapRef.current, {
          center: { lat: 40.7607334, lng: -73.8294727 },
          zoom: 16,
          mapTypeControl: false,
        });

        let markerArray = [];

        for (let i = 0; i < datas.length; i++) {
          let markerData = datas[i];

          // Create InfoWindow with conditional content
          const infoWindow = new window.google.maps.InfoWindow({
            content:
              markerData.classification === "Button9"
                ? `
                <div class="info-window">
                  <img src="${unionMarker}" class="union-marker-icon" alt="Union Center" />
                  <div class="location-name">${markerData.name}</div>
                  ${
                    markerData.address
                      ? `<div class="location-address">${markerData.address}</div>`
                      : ""
                  }
                </div>
              `
                : `
                <div class="info-window">
                  <div class="location-name">${markerData.name}</div>
                  ${
                    markerData.address
                      ? `<div class="location-address">${markerData.address}</div>`
                      : ""
                  }
                </div>
              `,
            disableAutoPan: true,
          });

          const marker = new Marker({
            map: mapInstance,
            position: {
              lat: markerData.position.lat,
              lng: markerData.position.lng,
            },
            visible: true,
            icon: {
              url:
                markerData.classification === "Button9"
                  ? unionMarker
                  : markerIcon,
              scaledSize: new window.google.maps.Size(
                markerData.classification === "Button9" ? 50 : 16,
                markerData.classification === "Button9" ? 50 : 16
              ),
            },
            title: markerData.name,
            classification: markerData.classification,
          });

          // Add hover listeners
          marker.addListener("mouseover", () => {
            if (activeInfoWindow) {
              activeInfoWindow.close();
            }
            infoWindow.open(mapInstance, marker);
            setActiveInfoWindow(infoWindow);
          });

          marker.addListener("mouseout", () => {
            infoWindow.close();
            setActiveInfoWindow(null);
          });

          markerArray.push(marker);
        }

        setMarkers(markerArray);

        // Apply map styles
        const styles = [
          {
            featureType: "all",
            elementType: "geometry",
            stylers: [{ color: "#ad885d" }, { lightness: 80 }],
          },
          {
            featureType: "all",
            elementType: "labels.text.fill",
            stylers: [
              { color: "#ad885d" }, // Make all text brown
            ],
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [
              { color: "#000000" }, // Light green color for parks
            ],
          },
          {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "poi.business",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#ffffff" }, { weight: 1 }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#91daee" }],
          },
          {
            featureType: "poi.park",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [
              { color: "#a5d6a7" }, // Light green color for parks
            ],
          },
        ];

        mapInstance.setOptions({ styles: styles });
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    };

    initializeMap();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    markers.forEach((marker) => {
      // Always show Button9 marker, otherwise check the button state
      if (marker.classification === "Button9") {
        marker.setVisible(true);
      } else if (selectedButtonArray[marker.classification]) {
        marker.setVisible(true);
      } else {
        marker.setVisible(false);
      }
    });
  }, [selectedButtonArray, markers]);

  return (
    <>
      <div className="h-[800px] relative">
        <div className="-ml-3 p-0 sm:p-6 lg:absolute top-4 right-14 z-10">
          <ul className="flex flex-wrap gap-3">
            {Object.entries(buttonLabels).map(([buttonName, label]) => (
              <li>
                <button
                  key={buttonName}
                  onClick={ButtonClick}
                  value={label}
                  name={buttonName}
                  className={`${getButtonClass(
                    buttonName
                  )} w-full text-xs px-2 py-1`}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div
          ref={mapRef}
          style={{ width: "100%", height: "800px" }} // Set the desired height and width
        ></div>
      </div>
    </>
  );
};

export default GoogleMapComponent;
